import { CheckIcon, CopyIcon } from '@chakra-ui/icons';
import {
  Box,
  Divider,
  Flex,
  Heading,
  IconButton,
  Link,
} from '@chakra-ui/react';
import { useMemo, useState } from 'react';
import { CopyToClipboard } from 'react-copy-to-clipboard';

import type {
  SelectionList,
  Item as SelectionListItem,
} from '@revelio/iso-utility';
import { Text } from '@revelio/ui';

import { useSelectionListsForKeys } from '../../shared/selection-lists/hooks/useSelectionListsForKeys';
import type { GqlSelectionListKey } from '../../shared/selection-lists/types';
import {
  buildFieldGranularityMap,
  buildFieldLabelMap,
} from './api/columns-api-helpers';
import type {
  DataBuilderStatus as DataBuilderApiStatus,
  DataBuilderPayload,
  DataBuilderStatusGetResponse,
} from './api/data-builder-api.types';
import { useDataBuilderQuery } from './api/useDataBuilderQuery';
import { useRoleSelectionLists } from './api/useRoleSelectionLists';
import { useSkillSelectionLists } from './api/useSkillSelectionLists';
import {
  classifyKey,
  resolveSelectionListItemLabel,
  toFetchKey,
} from './components/custom-filters/data-builder-selection-list-mapping';
import type { ConfigSummaryDetail } from './components/ui/ConfigSummaryList';
import { ConfigSummaryList } from './components/ui/ConfigSummaryList';
import type { DatasetSummaryData } from './components/ui/DatasetSummaryCard';
import { DatasetSummaryCard } from './components/ui/DatasetSummaryCard';
import { isDeliveryDownloadable } from './DataBuilderStatus';
import type {
  CustomFilterCondition,
  DeliveryMethod,
  ModelVersionSelections,
} from './data-builder.store';
import type { ConfirmationSummaryFromPayload } from './steps/buildConfirmationSummaryFromPayload';
import { buildConfirmationSummaryFromPayload } from './steps/buildConfirmationSummaryFromPayload';
import {
  getVersionDisplayLabel,
  isLatestVersions,
} from './steps/step2-column/model-utils';
import { dataLagDisplayValue } from './utils/data-lag-helper';

function extractSelectionListKeys(summaries: DatasetSummaryData[]): string[] {
  const keys = new Set<string>();
  const collectFromConditions = (conditions: CustomFilterCondition[]) => {
    for (const c of conditions) {
      if (c.type === 'selection_list' && c.selectionListItems?.length) {
        for (const item of c.selectionListItems) {
          keys.add(String(toFetchKey(item.selectionListId)));
        }
      }
    }
  };
  for (const ds of summaries) {
    // Unit of analysis selections
    for (const item of ds.unitOfAnalysisSelections) {
      if (item.selectionListId) {
        keys.add(String(toFetchKey(item.selectionListId)));
      }
    }
    for (const group of ds.filterGroups) {
      for (const f of group.filters) collectFromConditions(f.conditions);
    }
    for (const group of ds.customColumnGroups) {
      for (const col of group.customColumns) {
        for (const step of col.steps) {
          if (step.case) collectFromConditions(step.case.conditions);
        }
      }
    }
  }
  return Array.from(keys);
}

function useStatusSelectionLists(
  summary: ConfirmationSummaryFromPayload | null
): SelectionList[] {
  const allKeys = useMemo(
    () => (summary ? extractSelectionListKeys(summary.datasetSummaries) : []),
    [summary]
  );

  const modelVersions = (summary?.modelVersions ??
    {}) as ModelVersionSelections;
  const jobTaxVersion = String(modelVersions.job_taxonomy_version ?? '');
  const skillTaxVersion = String(modelVersions.skill_taxonomy_version ?? '');

  const { gqlKeys, roleKeys, skillKeys } = useMemo(() => {
    const gql: GqlSelectionListKey[] = [];
    const role: string[] = [];
    const skill: string[] = [];
    for (const k of allKeys) {
      const cat = classifyKey(k);
      if (cat === 'role') role.push(k);
      else if (cat === 'skill') skill.push(k);
      else gql.push(k as GqlSelectionListKey);
    }
    return { gqlKeys: gql, roleKeys: role, skillKeys: skill };
  }, [allKeys]);

  // Standard GQL lists. Router: RICS/geo → taxonomyTree, demographics →
  // referenceLists. Skill (V2 + V3) is handled by useSkillSelectionLists below.
  const { selectionLists: gqlState } = useSelectionListsForKeys(gqlKeys);

  // Role lists
  const { roleSelectionLists } = useRoleSelectionLists(roleKeys, {
    jobTaxonomyVersion: jobTaxVersion,
    taxonomyId: modelVersions.job_taxonomy_id as number | undefined,
  });

  // V3 Skill lists
  const { skillSelectionLists } = useSkillSelectionLists(skillKeys, {
    skillTaxonomyVersion: skillTaxVersion,
    taxonomyId: modelVersions.skill_taxonomy_id as number | undefined,
  });

  return useMemo(() => {
    const result: SelectionList[] = [];

    if (gqlState) {
      for (const key of gqlKeys) {
        const list = gqlState[key as keyof typeof gqlState] as
          | SelectionListItem[]
          | undefined;
        if (list) {
          const normalizedValue = (list[0] as unknown as SelectionList)?.label
            ? list
            : list.map((item) => ({ ...item, label: item.longName }));
          result.push({
            id: key,
            label: key,
            value: normalizedValue,
          } as SelectionList);
        }
      }
    }

    if (roleSelectionLists) {
      Object.values(roleSelectionLists).forEach((list) => {
        if (list) result.push(list as SelectionList);
      });
    }

    if (skillSelectionLists) {
      Object.values(skillSelectionLists).forEach((list) => {
        if (list) result.push(list);
      });
    }

    return result;
  }, [gqlState, gqlKeys, roleSelectionLists, skillSelectionLists]);
}

const DELIVERY_METHOD_LABELS: Record<DeliveryMethod, string> = {
  download: 'Download',
  snowflake: 'Snowflake',
  s3: 'AWS S3',
};

type DeliveryLocationMap = NonNullable<
  DataBuilderStatusGetResponse[number]['snowflake_tables']
>;

const RowCopyButton = ({ value }: { value: string }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <CopyToClipboard text={value} onCopy={handleCopy}>
      <IconButton
        icon={
          copied ? <CheckIcon color="green.500" boxSize="3" /> : <CopyIcon />
        }
        aria-label="copy value"
        variant="unstyled"
        size="xs"
        minW="auto"
        height="auto"
        ml="1"
        verticalAlign="middle"
        color={copied ? 'green.500' : 'navyBlue.500'}
        visibility="hidden"
        _groupHover={{ visibility: 'visible' }}
      />
    </CopyToClipboard>
  );
};

const humanizeKey = (key: string): string =>
  key
    .split('_')
    .filter(Boolean)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');

function getDeliveryLocationDetail(
  label: string,
  locations: DeliveryLocationMap,
  resolveLabel: (key: string) => string
): ConfigSummaryDetail | null {
  const datasetEntries = Object.entries(locations).filter(
    ([, datasetLocations]) => Object.keys(datasetLocations).length > 0
  );
  if (datasetEntries.length === 0) {
    return null;
  }

  return {
    label,
    value: (
      <Flex direction="column" gap="1.5">
        {datasetEntries.map(([datasetName, datasetLocations]) => {
          const locationEntries = Object.entries(datasetLocations);
          if (locationEntries.length === 1) {
            const [, fullPath] = locationEntries[0] ?? ['', ''];
            return (
              <Box
                key={datasetName}
                role="group"
                sx={{ wordBreak: 'break-word' }}
              >
                <b>{resolveLabel(datasetName)}</b>: {fullPath}
                <RowCopyButton value={fullPath} />
              </Box>
            );
          }
          return (
            <Flex key={datasetName} direction="column" gap="0.5">
              <b>{resolveLabel(datasetName)}</b>
              {locationEntries.map(([locationKey, fullPath]) => (
                <Box
                  key={locationKey}
                  role="group"
                  pl="8px"
                  sx={{ wordBreak: 'break-word' }}
                >
                  {resolveLabel(locationKey)}: {fullPath}
                  <RowCopyButton value={fullPath} />
                </Box>
              ))}
            </Flex>
          );
        })}
      </Flex>
    ),
  };
}

type DataBuilderConfigSummaryProps = {
  payload: DataBuilderPayload;
  status: DataBuilderApiStatus;
  url: string | null;
  snowflakeTables?: DataBuilderStatusGetResponse[number]['snowflake_tables'];
  s3Paths?: DataBuilderStatusGetResponse[number]['s3_paths'];
  /**
   * When false, the resolution memos are skipped (used by callers that mount
   * the component lazily, e.g. inside a closed modal). Defaults to true.
   */
  active?: boolean;
};

/**
 * Renders the read-only "Datasets" + "Delivery" summary for a Data Builder
 * payload. Shared by the status details modal and the share-redeem page so
 * both surfaces resolve labels (columns, filters, selection lists, model
 * versions) identically without touching the wizard store.
 */
export const DataBuilderConfigSummary = ({
  payload,
  status,
  url,
  snowflakeTables = null,
  s3Paths = null,
  active = true,
}: DataBuilderConfigSummaryProps) => {
  const { data } = useDataBuilderQuery();
  const models = useMemo(() => data?.models ?? [], [data?.models]);
  const datasets = useMemo(() => data?.datasets ?? [], [data?.datasets]);
  const datasetLabelById = useMemo(
    () => new Map(datasets.map((d) => [d.id, d.label] as const)),
    [datasets]
  );
  const deliverablesConfig = useMemo(
    () => data?.deliverablesConfig ?? {},
    [data?.deliverablesConfig]
  );

  const summary = useMemo(
    () => (active ? buildConfirmationSummaryFromPayload(payload) : null),
    [active, payload]
  );

  // Resolve model version labels, field labels, breakdown labels using API data
  const enhancedSummary = useMemo(() => {
    if (!summary) return null;

    const allLatest = isLatestVersions(summary.modelVersions, models);
    const resolvedModelEntries = Object.entries(summary.modelVersions).map(
      ([key, value]) => {
        const model = models.find((m) => m.key === key);
        const modelLabel = model?.name ?? humanizeKey(key);
        const versionLabel =
          model && (typeof value === 'number' || typeof value === 'string')
            ? getVersionDisplayLabel(model, value)
            : String(value);
        return { modelLabel, versionLabel };
      }
    );

    const datasetSummaries = summary.datasetSummaries.map((ds) => {
      const deliverableConfig = deliverablesConfig[ds.datasetKey];

      // Resolve column breakdown labels
      const columns = ds.columns.map((col) => {
        if (col.breakdownId === 'aggregation') return col;
        const breakdown = deliverableConfig?.breakdowns.find(
          (b) => b.id === col.breakdownId
        );
        return breakdown ? { ...col, breakdownLabel: breakdown.label } : col;
      });

      // Granularities
      const granularities = ds.columns
        .filter((col) => col.breakdownId !== 'aggregation')
        .map((col) => {
          const breakdown = deliverableConfig?.breakdowns.find(
            (b) => b.id === col.breakdownId
          );
          return breakdown?.label ?? col.breakdownLabel;
        });

      // Resolve unit of analysis label
      const unitOfAnalysis = (() => {
        if (!ds.unitOfAnalysis || ds.unitOfAnalysis === 'default') {
          return ds.unitOfAnalysis;
        }
        const option = deliverableConfig?.unitOfAnalysis.options.find(
          (opt) => opt.value === ds.unitOfAnalysis
        );
        return option?.label ?? ds.unitOfAnalysis;
      })();

      // Build unified field label map from ALL filter definitions
      const allFilterDefs = [
        ...(deliverableConfig?.generalFilters.customFilterDefinitions ?? []),
        ...(deliverableConfig?.breakdowns.flatMap(
          (b) => b.customFilterDefinitions
        ) ?? []),
        ...(deliverableConfig?.breakdowns.flatMap(
          (b) => b.customColumnFilterDefinitions
        ) ?? []),
      ];
      const unifiedFieldLabelMap = buildFieldLabelMap(allFilterDefs);
      const unifiedFieldGranularityMap =
        buildFieldGranularityMap(allFilterDefs);

      const filterGroups = ds.filterGroups.map((group) => {
        const breakdown =
          group.breakdownLabel !== null
            ? deliverableConfig?.breakdowns.find(
                (b) =>
                  b.label === group.breakdownLabel ||
                  b.id === group.breakdownLabel
              )
            : undefined;
        return {
          ...group,
          ...(breakdown ? { breakdownLabel: breakdown.label } : {}),
          fieldLabelMap: unifiedFieldLabelMap,
          fieldGranularityMap: unifiedFieldGranularityMap,
        };
      });

      const customColumnGroups = ds.customColumnGroups.map((group) => {
        const breakdown = deliverableConfig?.breakdowns.find(
          (b) =>
            b.label === group.breakdownLabel || b.id === group.breakdownLabel
        );
        return {
          ...group,
          ...(breakdown ? { breakdownLabel: breakdown.label } : {}),
          fieldLabelMap: unifiedFieldLabelMap,
          fieldGranularityMap: unifiedFieldGranularityMap,
        };
      });

      const label =
        ds.label && ds.label !== ds.datasetKey
          ? ds.label
          : (datasetLabelById.get(ds.datasetKey) ?? humanizeKey(ds.datasetKey));

      return {
        ...ds,
        label,
        unitOfAnalysis,
        columns,
        granularities,
        filterGroups,
        customColumnGroups,
        modelVersions: resolvedModelEntries,
        allLatestModelVersions: allLatest,
      };
    });

    return { ...summary, datasetSummaries };
  }, [summary, models, deliverablesConfig, datasetLabelById]);

  const selectionLists = useStatusSelectionLists(summary);

  // Resolve unit of analysis selection labels (IDs -> names)
  const finalSummary = useMemo(() => {
    if (!enhancedSummary || selectionLists.length === 0) return enhancedSummary;

    return {
      ...enhancedSummary,
      datasetSummaries: enhancedSummary.datasetSummaries.map((ds) => ({
        ...ds,
        unitOfAnalysisSelections: ds.unitOfAnalysisSelections.map((item) => {
          if (item.label !== item.id) return item;
          const resolved = resolveSelectionListItemLabel(
            item.id,
            item.selectionListId,
            selectionLists
          );
          return resolved ? { ...item, label: resolved } : item;
        }),
      })),
    };
  }, [enhancedSummary, selectionLists]);

  if (finalSummary === null) {
    return null;
  }

  const {
    datasetSummaries,
    deliveryMethod,
    deliveryFormat,
    snowflakeConfig,
    s3Config,
    lagData,
  } = finalSummary;

  const resolveLocationLabel = (key: string) =>
    datasetLabelById.get(key) ?? humanizeKey(key);

  const deliveryDetails: ConfigSummaryDetail[] = (() => {
    const methodLabel = DELIVERY_METHOD_LABELS[deliveryMethod];
    const dataLagDetails =
      lagData > 0
        ? [
            {
              label: 'Data lag',
              value: dataLagDisplayValue(lagData),
            },
          ]
        : [];

    if (deliveryMethod === 'snowflake' && snowflakeConfig !== null) {
      const tableDetail =
        snowflakeTables !== null && snowflakeTables !== undefined
          ? getDeliveryLocationDetail(
              'Tables',
              snowflakeTables,
              resolveLocationLabel
            )
          : null;
      if (tableDetail !== null) {
        return [
          { label: 'Method', value: methodLabel },
          tableDetail,
          ...dataLagDetails,
        ];
      }
      // database/schema may be absent (filled from client settings server-side).
      const fullTablePath = [
        snowflakeConfig.database,
        snowflakeConfig.schema,
        snowflakeConfig.table,
      ]
        .filter((part): part is string => part !== undefined)
        .join('.');
      return [
        { label: 'Method', value: methodLabel },
        { label: 'Table', value: fullTablePath, copyValue: fullTablePath },
        ...dataLagDetails,
      ];
    }

    if (deliveryMethod === 's3' && s3Config !== null) {
      const pathDetail =
        s3Paths !== null && s3Paths !== undefined
          ? getDeliveryLocationDetail('Paths', s3Paths, resolveLocationLabel)
          : null;
      if (pathDetail !== null) {
        return [
          { label: 'Method', value: methodLabel },
          { label: 'Format', value: s3Config.format },
          pathDetail,
          ...dataLagDetails,
        ];
      }
      // The bucket may be absent (filled from client settings server-side);
      // fall back to the bare path without a misleading s3:// prefix.
      const s3CopyValue =
        s3Config.bucket !== undefined
          ? `s3://${s3Config.bucket}/${s3Config.path.replace(/^\//, '')}`
          : s3Config.path; // clients don't have bucket in their configurations, so copy the destination.
      return [
        { label: 'Method', value: methodLabel },
        { label: 'Path', value: s3CopyValue, copyValue: s3CopyValue },
        { label: 'Format', value: s3Config.format },
        ...dataLagDetails,
      ];
    }

    const downloadValue = isDeliveryDownloadable(status, url) ? (
      <Link
        href={url ?? undefined}
        target="_blank"
        rel="noopener noreferrer"
        color="text.primary"
        textDecoration="underline"
      >
        {`${methodLabel} (${deliveryFormat})`}
      </Link>
    ) : (
      `${methodLabel} (${deliveryFormat})`
    );

    return [{ label: 'Delivery', value: downloadValue }, ...dataLagDetails];
  })();

  const hasConfiguredDelivery = payload.deliverables.some(
    (deliverable) => deliverable.delivery.type !== 'empty'
  );

  return (
    <>
      <Heading fontWeight="600" fontSize="16px" color="text.primary">
        Datasets
      </Heading>

      {datasetSummaries.map((dataset) => (
        <DatasetSummaryCard
          key={dataset.instanceId}
          dataset={dataset}
          selectionLists={selectionLists}
        />
      ))}

      {datasetSummaries.length === 0 && (
        <Text fontSize="sm" style={{ paddingLeft: '20px' }}>
          No datasets selected
        </Text>
      )}

      {hasConfiguredDelivery && (
        <>
          <Divider my="4" />

          <Heading fontWeight="600" fontSize="16px" color="text.primary">
            Delivery
          </Heading>
          <ConfigSummaryList details={deliveryDetails} />
        </>
      )}
    </>
  );
};
